/// FOURJS_START_COPYRIGHT(D,2026)
/// Property of Four Js*
/// (c) Copyright Four Js 2026, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT

"use strict";

modulum("IndexedDbService", ["InitService"], function (context, cls) {
  const DB_NAME = "gbc";

  // Schema version: bump when SCHEMA changes so onupgradeneeded re-runs
  const DB_VERSION = 1;

  /**
   * Object stores schema. Declare every store here, then bump DB_VERSION.
   * Store config: keyPath, autoIncrement, indexes: [{name, keyPath, unique?, multiEntry?}]
   */
  const SCHEMA = {
    // AI conversation history (GBC-5835): metadata + one record per message
    conversations: {
      keyPath: "localId"
    },
    messages: {
      autoIncrement: true,
      indexes: [{ name: "byConversation", keyPath: "conversationId" }]
    }
  };

  /**
   * Declare IndexedDbService class
   */
  class IndexedDbService {
    /** @type {?IDBDatabase} */
    _db = null;
    /** @type {?Promise<IDBDatabase>} */
    _ready = null;

    /**
     * Whether IndexedDB exists in the current runtime
     * @returns {boolean}
     */
    isAvailable() {
      return Boolean(globalThis.indexedDB);
    }

    /**
     * Open the database (failures are logged, not thrown)
     */
    init() {
      if (!this.isAvailable()) {
        console.warn("IndexedDbService: IndexedDB is not available in this runtime");
        this._ready = null;
        return;
      }

      this._ready = this._open();
      this._ready.catch((error) => {
        console.error("IndexedDbService: failed to open database", error);
        this._db = null;
        this._ready = null;
      });
    }

    /**
     * Close the database and reset the service
     */
    destroy() {
      if (this._db) {
        this._db.close();
      }
      this._db = null;
      this._ready = null;
    }

    /**
     * Resolves once the database is open; rejects when IndexedDB is unavailable
     * @returns {Promise<IDBDatabase>}
     */
    ready() {
      return this._getDb();
    }

    /**
     * Open the database and apply SCHEMA on version upgrade
     * @returns {Promise<IDBDatabase>}
     * @private
     */
    _open() {
      return new Promise((resolve, reject) => {
        const request = globalThis.indexedDB.open(DB_NAME, DB_VERSION);

        request.onupgradeneeded = (event) => {
          this._applySchema(request.result, event.target.transaction);
        };
        request.onsuccess = () => {
          const db = request.result;
          this._db = db;
          // Close on version change so another tab's upgrade isn't blocked forever.
          db.onversionchange = () => {
            db.close();
            this._db = null;
            this._ready = null;
          };
          resolve(db);
        };
        request.onerror = () => {
          reject(request.error);
        };
        request.onblocked = () => {
          console.warn("IndexedDbService: database upgrade blocked by another open connection");
        };
      });
    }

    /**
     * Create stores/indexes declared in SCHEMA but missing from the database
     * @param {IDBDatabase} db
     * @param {IDBTransaction} transaction - active version-change transaction
     * @private
     */
    _applySchema(db, transaction) {
      for (const [storeName, config] of Object.entries(SCHEMA)) {
        let store = null;
        if (db.objectStoreNames.contains(storeName)) {
          store = transaction.objectStore(storeName);
        } else {
          const options = {};
          if (config.keyPath !== undefined) {
            options.keyPath = config.keyPath;
          }
          if (config.autoIncrement) {
            options.autoIncrement = true;
          }
          store = db.createObjectStore(storeName, options);
        }

        const indexes = config.indexes || [];
        for (const index of indexes) {
          if (!store.indexNames.contains(index.name)) {
            store.createIndex(index.name, index.keyPath, {
              unique: Boolean(index.unique),
              multiEntry: Boolean(index.multiEntry)
            });
          }
        }
      }
    }

    /**
     * Open-database promise, or a rejected promise when unavailable
     * @returns {Promise<IDBDatabase>}
     * @private
     */
    _getDb() {
      if (this._ready === null) {
        return Promise.reject(new Error("IndexedDbService: database is not available"));
      }
      return this._ready;
    }

    /**
     * Wrap an IDBRequest into a Promise of its result
     * @param {IDBRequest} request
     * @returns {Promise<*>}
     * @private
     */
    _promisify(request) {
      return new Promise((resolve, reject) => {
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });
    }

    /**
     * Read a single record by key
     * @param {string} storeName
     * @param {IDBValidKey} key
     * @returns {Promise<*>} the stored value, or undefined when absent
     */
    async get(storeName, key) {
      const db = await this._getDb();
      const store = db.transaction(storeName, "readonly").objectStore(storeName);
      return this._promisify(store.get(key));
    }

    /**
     * Read all records, optionally filtered by a key range
     * @param {string} storeName
     * @param {(IDBValidKey|IDBKeyRange)} [query]
     * @param {number} [count]
     * @returns {Promise<Array.<*>>}
     */
    async getAll(storeName, query, count) {
      const db = await this._getDb();
      const store = db.transaction(storeName, "readonly").objectStore(storeName);
      return this._promisify(store.getAll(query, count));
    }

    /**
     * Read all records matching a query on an index
     * @param {string} storeName
     * @param {string} indexName
     * @param {(IDBValidKey|IDBKeyRange)} [query]
     * @param {number} [count]
     * @returns {Promise<Array.<*>>}
     */
    async getAllFromIndex(storeName, indexName, query, count) {
      const db = await this._getDb();
      const index = db.transaction(storeName, "readonly").objectStore(storeName).index(indexName);
      return this._promisify(index.getAll(query, count));
    }

    /**
     * Insert or update a record (appends a new key on autoIncrement stores)
     * @param {string} storeName
     * @param {*} value
     * @param {IDBValidKey} [key] - for stores with out-of-line keys
     * @returns {Promise<IDBValidKey>}
     */
    async put(storeName, value, key) {
      const db = await this._getDb();
      const store = db.transaction(storeName, "readwrite").objectStore(storeName);
      if (key === undefined) {
        return this._promisify(store.put(value));
      }
      return this._promisify(store.put(value, key));
    }

    /**
     * Delete a record by key
     * @param {string} storeName
     * @param {IDBValidKey} key
     * @returns {Promise<void>}
     */
    async delete(storeName, key) {
      const db = await this._getDb();
      const store = db.transaction(storeName, "readwrite").objectStore(storeName);
      return this._promisify(store.delete(key));
    }

    /**
     * Delete every record matching a query on an index
     * @param {string} storeName
     * @param {string} indexName
     * @param {(IDBValidKey|IDBKeyRange)} [query]
     * @returns {Promise<void>}
     */
    async deleteAllFromIndex(storeName, indexName, query) {
      const db = await this._getDb();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(storeName, "readwrite");
        const store = transaction.objectStore(storeName);
        const keysRequest = store.index(indexName).getAllKeys(query);

        keysRequest.onsuccess = () => {
          for (const key of keysRequest.result) {
            store.delete(key);
          }
        };
        transaction.oncomplete = () => resolve();
        transaction.onerror = () => reject(transaction.error);
        transaction.onabort = () => reject(transaction.error);
      });
    }
  }

  /**
   * Generic IndexedDB persistence service (like StoredSettingsService wraps localStorage)
   * @class gbc.IndexedDbService
   * @gbcService
   */
  gbc.IndexedDbService = new IndexedDbService();

  context.InitService.register(context.IndexedDbService);
});
